import type { ScanPhoto } from '../ScanRoot';
import type { ScanGroupOptimized, ScanRootOptimized, ScanTaskOptimized } from '../ScanRootOptimized';
import { isGroupShownOnScopeReport, isTaskShownOnScopeReport } from './scopingLib';
import { getGroupsInStandardGroupOrder, getTasksInStandardSortOrder } from './StandardScanPresentation';

export const MILLISECONDS_PER_MINUTE = 60 * 1000;
export const MILLISECONDS_PER_QUARTER_HOUR = 15 * MILLISECONDS_PER_MINUTE;
export const MILLISECONDS_PER_HOUR = 60 * MILLISECONDS_PER_MINUTE;
export const MILLISECONDS_PER_DAY = 24 * MILLISECONDS_PER_HOUR;
export const MILLISECONDS_PER_WEEK = 7 * MILLISECONDS_PER_DAY;

/**
 * A local Sunday, so whole-week periods land on calendar weeks. Day blocks are counted from here
 * rather than from the Unix epoch, which was a Thursday.
 */
const DAY_BLOCK_ANCHOR = { year: 1970, month: 0, day: 4 };

/**
 * How wide a bucket one row covers. Minutes, hours, days and weeks are fixed spans; months are not,
 * so they are their own unit rather than an approximation in milliseconds.
 */
export type ScanPhotoTimelinePeriod =
  | { unit: 'duration'; milliseconds: number }
  | { unit: 'month' };

/**
 * Buckets too small for a renovation to fill, and there for exactly that reason: a scan's photos can
 * be spread over minutes during a dev session and still land in separate rows, where day- and
 * week-sized buckets would need a renovation's worth of real time to tell apart.
 */
export const SCAN_PHOTO_TIMELINE_MINUTE: ScanPhotoTimelinePeriod = { unit: 'duration', milliseconds: MILLISECONDS_PER_MINUTE };
export const SCAN_PHOTO_TIMELINE_QUARTER_HOUR: ScanPhotoTimelinePeriod = { unit: 'duration', milliseconds: MILLISECONDS_PER_QUARTER_HOUR };

export const SCAN_PHOTO_TIMELINE_HOUR: ScanPhotoTimelinePeriod = { unit: 'duration', milliseconds: MILLISECONDS_PER_HOUR };
export const SCAN_PHOTO_TIMELINE_DAY: ScanPhotoTimelinePeriod = { unit: 'duration', milliseconds: MILLISECONDS_PER_DAY };
export const SCAN_PHOTO_TIMELINE_WEEK: ScanPhotoTimelinePeriod = { unit: 'duration', milliseconds: MILLISECONDS_PER_WEEK };
export const SCAN_PHOTO_TIMELINE_MONTH: ScanPhotoTimelinePeriod = { unit: 'month' };

/** One column of the timeline: a group on the scope report, or one of its tasks on the scope report. */
export type ScanPhotoTimelineColumn = {
  groupId: string;
  /** undefined for the group's own column */
  taskId?: string;
};

/** The photos of one column within one row. */
export type ScanPhotoTimelineCell = ScanPhotoTimelineColumn & {
  photos: ScanPhoto[];
};

/** One time bucket. `cells` is index-aligned with the timeline's `columns`. */
export type ScanPhotoTimelineRow = {
  /** start of the bucket; undefined for the leading row of photos with no addedAt */
  periodStart?: number;
  cells: ScanPhotoTimelineCell[];
};

export type ScanPhotoTimeline = {
  period: ScanPhotoTimelinePeriod;
  columns: ScanPhotoTimelineColumn[];
  rows: ScanPhotoTimelineRow[];
};

type ScanPhotoTimelineColumnSource = ScanPhotoTimelineColumn & {
  undatedPhotos: ScanPhoto[];
  photosByPeriodStart: Map<number, ScanPhoto[]>;
};

const EMPTY_PHOTOS: ScanPhoto[] = [];

const getLocalDayStart = (timestamp: number): number => {
  const date = new Date(timestamp);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};

const getAnchoredDayStart = (dayOffset: number): number => new Date(DAY_BLOCK_ANCHOR.year, DAY_BLOCK_ANCHOR.month, DAY_BLOCK_ANCHOR.day + dayOffset).getTime();

/**
 * Whole local days between the anchor and `dayStart`. Both are local midnights, so a daylight-saving
 * shift only ever moves the quotient by an hour either way — rounding recovers the exact day count.
 */
const getAnchoredDayOffset = (dayStart: number): number => Math.round((dayStart - getAnchoredDayStart(0)) / MILLISECONDS_PER_DAY);

/**
 * The start of the clustering bucket containing `timestamp`.
 *
 * Buckets nest inside the local calendar day, so hour- and minute-sized periods align to the local
 * clock. Periods of a day or more bucket by whole local days — a week lands on calendar weeks
 * starting Sunday — and each bucket start is itself a local midnight, so daylight-saving shifts
 * neither straddle nor drift a bucket.
 */
export const getScanPhotoTimelinePeriodStart = (timestamp: number, period: ScanPhotoTimelinePeriod): number => {
  const date = new Date(timestamp);
  if (period.unit === 'month') return new Date(date.getFullYear(), date.getMonth(), 1).getTime();

  const dayStart = getLocalDayStart(timestamp);
  if (period.milliseconds < MILLISECONDS_PER_DAY) {
    return dayStart + Math.floor((timestamp - dayStart) / period.milliseconds) * period.milliseconds;
  }
  const dayCount = Math.max(1, Math.round(period.milliseconds / MILLISECONDS_PER_DAY));
  if (dayCount === 1) return dayStart;
  return getAnchoredDayStart(Math.floor(getAnchoredDayOffset(dayStart) / dayCount) * dayCount);
};

/**
 * Which tasks earn columns, and in what order: the ones the scope report lists, arranged the way it
 * arranges them — so the grid can never disagree with the report about what is part of the job, nor
 * about where in the job it sits.
 *
 * That is wider than "has a cost". A task that was only photographed — no cost, no measurements — is on
 * the scope report, and it is precisely the kind of task this grid is read for, so it gets a column of
 * its own rather than having its photos folded into the group's.
 *
 * Order comes from `getTasksInStandardSortOrder`, the same standard sort the scope report and the
 * KaiNavigator sidebar read. Filtering happens first so the sort only ever runs over a fresh array —
 * the standard sorts order their input in place.
 */
const getTimelineTasks = (group: ScanGroupOptimized): ScanTaskOptimized[] => {
  const { clusterOrder, taskClusters } = getTasksInStandardSortOrder(
    group.template,
    group.tasks?.filter(isTaskShownOnScopeReport) ?? [],
  );
  return clusterOrder.flatMap(category => taskClusters[category] ?? []);
};

/**
 * The groups with columns, in standard group order — `getGroupsInStandardGroupOrder`, as the scope
 * report and the sidebar use it.
 */
const getTimelineGroups = (scanRoot: ScanRootOptimized, onlyGroupId?: string): ScanGroupOptimized[] => {
  const { clusterOrder, groupClusters } = getGroupsInStandardGroupOrder(
    scanRoot.template,
    scanRoot.groups?.filter(group => (onlyGroupId == null || group.id === onlyGroupId) && isGroupShownOnScopeReport(group)) ?? [],
  );
  return clusterOrder.flatMap(category => groupClusters[category] ?? []);
};

/**
 * Everything in the group's subtree that no task column already shows. A photo attached to both a group
 * and one of its own columned tasks belongs to the task; a photo on a task the report leaves out —
 * `notApplicable` — still surfaces here, so none are lost.
 */
const getGroupColumnPhotos = (group: ScanGroupOptimized, columnTasks: ScanTaskOptimized[]): ScanPhoto[] => {
  const taskPhotoIds = new Set(columnTasks.flatMap(task => task.optimized.allPhotos.map(photo => photo.photoId)));
  return group.optimized.allPhotos.filter(photo => !taskPhotoIds.has(photo.photoId));
};

const byAddedAt = (a: ScanPhoto, b: ScanPhoto) => (a.addedAt ?? 0) - (b.addedAt ?? 0);

const getColumnSource = (
  column: ScanPhotoTimelineColumn,
  photos: ScanPhoto[],
  period: ScanPhotoTimelinePeriod,
): ScanPhotoTimelineColumnSource => {
  const undatedPhotos: ScanPhoto[] = [];
  const photosByPeriodStart = new Map<number, ScanPhoto[]>();
  for (const photo of photos) {
    if (photo.addedAt == null) {
      undatedPhotos.push(photo);
      continue;
    }
    const periodStart = getScanPhotoTimelinePeriodStart(photo.addedAt, period);
    const bucket = photosByPeriodStart.get(periodStart);
    if (bucket) bucket.push(photo);
    else photosByPeriodStart.set(periodStart, [photo]);
  }
  for (const bucket of photosByPeriodStart.values()) bucket.sort(byAddedAt);
  return { ...column, undatedPhotos, photosByPeriodStart };
};

/**
 * The group's own column and its task columns — or nothing at all, when the group has neither a task on
 * the scope report nor a photo of its own.
 *
 * A group can be on the scope report and still have nothing to show here: carrying a cost is enough to
 * list it, and that is a statement about money rather than about work anyone can look at. A lone column
 * of empty cells says nothing, so it isn't drawn.
 */
const getGroupColumnSources = (
  group: ScanGroupOptimized,
  period: ScanPhotoTimelinePeriod,
): ScanPhotoTimelineColumnSource[] => {
  const columnTasks = getTimelineTasks(group);
  const groupPhotos = getGroupColumnPhotos(group, columnTasks);
  if (columnTasks.length === 0 && groupPhotos.length === 0) return [];
  return [
    getColumnSource({ groupId: group.id }, groupPhotos, period),
    ...columnTasks.map(task => getColumnSource({ groupId: group.id, taskId: task.id }, task.optimized.allPhotos, period)),
  ];
};

const getColumnSources = (
  scanRoot: ScanRootOptimized,
  period: ScanPhotoTimelinePeriod,
  onlyGroupId?: string,
): ScanPhotoTimelineColumnSource[] => getTimelineGroups(scanRoot, onlyGroupId).flatMap(group => getGroupColumnSources(group, period));

const getSortedPeriodStarts = (columnSources: ScanPhotoTimelineColumnSource[]): number[] =>
  [...new Set(columnSources.flatMap(source => [...source.photosByPeriodStart.keys()]))].sort((a, b) => a - b);

const getRow = (
  periodStart: number | undefined,
  columnSources: ScanPhotoTimelineColumnSource[],
): ScanPhotoTimelineRow => ({
  periodStart,
  cells: columnSources.map(({ groupId, taskId, undatedPhotos, photosByPeriodStart }) => ({
    groupId,
    taskId,
    photos: periodStart == null ? undatedPhotos : photosByPeriodStart.get(periodStart) ?? EMPTY_PHOTOS,
  })),
});

/**
 * Clusters a scan's photos into a grid: rows are time buckets, columns are the groups and tasks the
 * scope report shows — scoped, measured, or merely photographed.
 *
 * Every row carries the same columns, so `row.cells` is index-aligned with `columns` and a column
 * with nothing in that bucket gets an empty photo list. Each such group contributes its own
 * column followed by one column per task of its own on the report.
 *
 * Columns run left to right in the standard sort orders — `getGroupsInStandardGroupOrder` for the
 * groups, `getTasksInStandardSortOrder` for each group's tasks. Those are what the scope report and the
 * KaiNavigator sidebar are arranged by, and reading a column against the sidebar row that names it is
 * the whole point of the grid, so the two have to be in the same order.
 *
 * Photos with no `addedAt` count as the oldest and gather into a single leading row whose
 * `periodStart` is undefined. Dated rows follow in ascending order, as do the photos within a cell,
 * so the newest photo of a cell is last. Only buckets holding at least one photo become rows.
 *
 * @param scanRoot the optimized scan to read
 * @param period bucket width, defaulting to one day; see `getScanPhotoTimelinePeriodStart`
 * @param groupId restricts the timeline to a single group when given
 */
export const getScanPhotoTimeline = (
  scanRoot: ScanRootOptimized,
  { period = SCAN_PHOTO_TIMELINE_DAY, groupId }: { period?: ScanPhotoTimelinePeriod; groupId?: string } = {},
): ScanPhotoTimeline => {
  const columnSources = getColumnSources(scanRoot, period, groupId);
  return {
    period,
    columns: columnSources.map(source => ({ groupId: source.groupId, taskId: source.taskId })),
    rows: [
      ...(columnSources.some(source => source.undatedPhotos.length > 0) ? [getRow(undefined, columnSources)] : []),
      ...getSortedPeriodStarts(columnSources).map(periodStart => getRow(periodStart, columnSources)),
    ],
  };
};
